import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Gamepad2, 
  Target, 
  BookOpen, 
  ArrowRight, 
  Star, 
  Clock, 
  Users,
  TrendingUp,
  Heart,
  Zap,
  CheckCircle,
  Info
} from 'lucide-react';
import { gameModes, challengeScenarios, educationalModules } from '../data/gameModes';
import { GameModeConfig, ChallengeScenario, EducationalModule } from '../types/gameModes';

interface GameModeSelectorProps {
  onModeSelected: (mode: string, scenario?: string, module?: string) => void;
  onBack: () => void;
}

const GameModeSelector: React.FC<GameModeSelectorProps> = ({ onModeSelected, onBack }) => {
  const [selectedMode, setSelectedMode] = useState<string | null>(null);
  const [selectedScenario, setSelectedScenario] = useState<string | null>(null);
  const [selectedModule, setSelectedModule] = useState<string | null>(null);

  const getModeIcon = (mode: string) => {
    switch (mode) {
      case 'sandbox': return <Gamepad2 size={24} />;
      case 'challenge': return <Target size={24} />;
      case 'educational': return <BookOpen size={24} />;
      default: return <Gamepad2 size={24} />;
    }
  };

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy': return 'text-green-400 bg-green-400/20';
      case 'medium': return 'text-yellow-400 bg-yellow-400/20';
      case 'hard': return 'text-red-400 bg-red-400/20';
      default: return 'text-slate-400 bg-slate-400/20';
    }
  };

  const getDifficultyIcon = (difficulty: string) => {
    switch (difficulty) {
      case 'easy': return '🟢';
      case 'medium': return '🟡';
      case 'hard': return '🔴';
      default: return '⚪';
    }
  };

  const handleStartGame = () => {
    if (selectedMode) {
      onModeSelected(selectedMode, selectedScenario || undefined, selectedModule || undefined);
    }
  };

  const renderModeCard = (mode: GameModeConfig) => (
    <motion.div
      key={mode.id}
      whileHover={{ scale: 1.02, y: -4 }}
      whileTap={{ scale: 0.98 }}
      onClick={() => setSelectedMode(mode.id)}
      className={`relative p-6 rounded-2xl border-2 cursor-pointer transition-all duration-300 ${
        selectedMode === mode.id
          ? 'border-blue-500 bg-blue-500/10 shadow-lg shadow-blue-500/25'
          : 'border-slate-700 bg-slate-800/50 hover:border-slate-600 hover:bg-slate-800/70'
      }`}
    >
      <div className="flex items-center space-x-4 mb-4">
        <div className={`p-3 rounded-xl bg-gradient-to-r ${mode.gradient} text-white`}>
          {getModeIcon(mode.id)}
        </div>
        <div>
          <h3 className="text-xl font-bold text-white">{mode.name}</h3>
          <p className="text-slate-400 text-sm">{mode.description}</p>
        </div>
      </div>
      
      <div className="space-y-3">
        <div className="flex items-center space-x-2 text-sm text-slate-300">
          <Star size={16} className="text-yellow-400" />
          <span>Key Objectives:</span>
        </div>
        <ul className="space-y-1 ml-6">
          {mode.objectives?.slice(0, 3).map((objective, index) => (
            <li key={index} className="text-sm text-slate-400 flex items-center space-x-2">
              <CheckCircle size={12} className="text-green-400" />
              <span>{objective}</span>
            </li>
          ))}
        </ul>
      </div>

      {selectedMode === mode.id && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="absolute top-2 right-2"
        >
          <CheckCircle size={20} className="text-blue-400" />
        </motion.div>
      )}
    </motion.div>
  );

  const renderScenarioCard = (scenario: ChallengeScenario) => (
    <motion.div
      key={scenario.id}
      whileHover={{ scale: 1.02, y: -2 }}
      whileTap={{ scale: 0.98 }}
      onClick={() => setSelectedScenario(scenario.id)}
      className={`relative p-5 rounded-xl border-2 cursor-pointer transition-all duration-300 ${
        selectedScenario === scenario.id
          ? 'border-orange-500 bg-orange-500/10 shadow-lg shadow-orange-500/25'
          : 'border-slate-700 bg-slate-800/50 hover:border-slate-600 hover:bg-slate-800/70'
      }`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center space-x-3">
          <div className={`p-2 rounded-lg bg-gradient-to-r ${scenario.gradient} text-white text-lg`}>
            {scenario.icon}
          </div>
          <div>
            <h4 className="font-semibold text-white">{scenario.name}</h4>
            <p className="text-sm text-slate-400">{scenario.description}</p>
          </div>
        </div>
        <div className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(scenario.difficulty)}`}>
          {getDifficultyIcon(scenario.difficulty)} {scenario.difficulty}
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-center space-x-2 text-xs text-slate-300">
          <Target size={12} />
          <span>Main Goals:</span>
        </div>
        <ul className="space-y-1 ml-4">
          {scenario.objectives.slice(0, 2).map((objective, index) => (
            <li key={index} className="text-xs text-slate-400 flex items-center space-x-2">
              <CheckCircle size={10} className="text-green-400" />
              <span>{objective}</span>
            </li>
          ))}
        </ul>
      </div>

      {selectedScenario === scenario.id && (
        <motion.div
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          className="absolute top-2 right-2"
        >
          <CheckCircle size={16} className="text-orange-400" />
        </motion.div>
      )}
    </motion.div>
  );

  const renderModuleCard = (module: EducationalModule) => (
    <motion.div
      key={module.id}
      whileHover={{ scale: 1.02, y: -2 }}
      whileTap={{ scale: 0.98 }}
      onClick={() => setSelectedModule(module.id)}
      className={`relative p-5 rounded-xl border-2 cursor-pointer transition-all duration-300 ${
        selectedModule === module.id
          ? 'border-emerald-500 bg-emerald-500/10 shadow-lg shadow-emerald-500/25'
          : 'border-slate-700 bg-slate-800/50 hover:border-slate-600 hover:bg-slate-800/70'
      }`}
    >
      <div className="flex items-center space-x-3 mb-3">
        <div className={`p-2 rounded-lg bg-gradient-to-r ${module.gradient} text-white text-lg`}>
          {module.icon}
        </div>
        <div>
          <h4 className="font-semibold text-white">{module.name}</h4>
          <p className="text-sm text-slate-400">{module.description}</p>
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-center space-x-2 text-xs text-slate-300">
          <BookOpen size={12} />
          <span>Focus Areas:</span>
        </div>
        <div className="flex flex-wrap gap-1 ml-4">
          {module.focus.slice(0, 3).map((focus, index) => (
            <span key={index} className="px-2 py-1 bg-slate-700/50 text-xs text-slate-300 rounded-full">
              {focus}
            </span>
          ))}
        </div>
      </div>

      {selectedModule === module.id && (
        <motion.div
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          className="absolute top-2 right-2"
        >
          <CheckCircle size={16} className="text-emerald-400" />
        </motion.div>
      )}
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <h1 className="text-4xl font-bold text-white mb-4">
            Choose Your Life Journey
          </h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Select a game mode that matches your learning goals and play style. 
            Each mode offers a unique experience with different objectives and challenges.
          </p>
        </motion.div>

        {/* Game Modes */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-8"
        >
          <h2 className="text-2xl font-bold text-white mb-6 flex items-center space-x-3">
            <Gamepad2 size={28} className="text-blue-400" />
            <span>Game Modes</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {gameModes.map(renderModeCard)}
          </div>
        </motion.div>

        {/* Challenge Scenarios */}
        <AnimatePresence>
          {selectedMode === 'challenge' && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mb-8"
            >
              <h2 className="text-2xl font-bold text-white mb-6 flex items-center space-x-3">
                <Target size={28} className="text-orange-400" />
                <span>Challenge Scenarios</span>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {challengeScenarios.map(renderScenarioCard)}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Educational Modules */}
        <AnimatePresence>
          {selectedMode === 'educational' && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mb-8"
            >
              <h2 className="text-2xl font-bold text-white mb-6 flex items-center space-x-3">
                <BookOpen size={28} className="text-emerald-400" />
                <span>Educational Modules</span>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {educationalModules.map(renderModuleCard)}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="flex justify-center space-x-4"
        >
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onBack}
            className="px-8 py-3 bg-slate-700 hover:bg-slate-600 text-white rounded-xl font-semibold transition-colors duration-300"
          >
            Back to Menu
          </motion.button>
          
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleStartGame}
            disabled={!selectedMode || (selectedMode === 'challenge' && !selectedScenario) || (selectedMode === 'educational' && !selectedModule)}
            className={`px-8 py-3 rounded-xl font-semibold transition-all duration-300 flex items-center space-x-2 ${
              selectedMode && ((selectedMode === 'challenge' && selectedScenario) || (selectedMode === 'educational' && selectedModule) || selectedMode === 'sandbox')
                ? 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white shadow-lg shadow-blue-500/25'
                : 'bg-slate-600 text-slate-400 cursor-not-allowed'
            }`}
          >
            <span>Start Journey</span>
            <ArrowRight size={20} />
          </motion.button>
        </motion.div>

        {/* Info Panel */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="mt-12 p-6 bg-slate-800/50 rounded-2xl border border-slate-700"
        >
          <div className="flex items-start space-x-4">
            <Info size={24} className="text-blue-400 mt-1" />
            <div>
              <h3 className="text-lg font-semibold text-white mb-2">Game Mode Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-slate-300">
                <div>
                  <h4 className="font-medium text-blue-400 mb-1">Sandbox Mode</h4>
                  <p>Complete freedom to explore different life paths and set your own goals. Perfect for experimentation and learning through trial and error.</p>
                </div>
                <div>
                  <h4 className="font-medium text-orange-400 mb-1">Challenge Mode</h4>
                  <p>Take on specific scenarios with defined objectives and starting conditions. Test your strategic thinking and decision-making skills.</p>
                </div>
                <div>
                  <h4 className="font-medium text-emerald-400 mb-1">Educational Mode</h4>
                  <p>Guided learning experience with lessons, questions, and reflection points. Ideal for workshops and structured learning environments.</p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default GameModeSelector;
